import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  AfterViewInit,
  ChangeDetectorRef,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { Meta, Title } from '@angular/platform-browser';
import { environment } from '../../../environments/environment';
import { BehaviorSubject, Observable, catchError, of, tap, finalize } from 'rxjs';

interface Shop {
  id: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
  category: string;
  description?: string;
  url?: string; // Company website
  voucherUrl?: string; // Schenkwerk storefront URL (if storefront enabled)
  isSchenkwerkPartner?: boolean;
  logoUrl?: string; // Relative path from API, use getLogoUrl() for full URL
}

// Category mapping from backend values to German display names
const CATEGORY_MAP: { [key: string]: string } = {
  restaurant: 'Restaurant',
  cafe: 'Café',
  bakery: 'Bäckerei',
  bar: 'Bar',
  florist: 'Blumenladen',
  gift: 'Geschenkeladen',
  perfumery: 'Parfümerie',
  cosmetics: 'Kosmetik',
  wine: 'Weinhandlung',
  boutique: 'Boutique',
  coffee: 'Café',
  pastry: 'Konditorei',
  confectionery: 'Konditorei',
  // Map remaining to existing or "Sonstiges"
  deli: 'Feinkost',
  convenience: 'Sonstiges',
  tea: 'Café',
  alcohol: 'Weinhandlung',
  spa: 'Wellness & Spa',
  books: 'Sonstiges',
  farm: 'Feinkost',
  yes: 'Sonstiges',
  baby_goods: 'Sonstiges',
  beauty: 'Kosmetik',
  bicycle: 'Sonstiges',
  butcher: 'Feinkost',
  chocolate: 'Konditorei',
  'deli, bar': 'Feinkost',
  department_store: 'Sonstiges',
  fabric: 'Sonstiges',
  ice_cream: 'Café',
  interior_decoration: 'Sonstiges',
  music: 'Sonstiges',
  seafood: 'Feinkost',
  spices: 'Feinkost',
  sports: 'Sonstiges',
  supermarket: 'Sonstiges',
  'tea; coffee; alcoholics': 'Café',
};

// Category icon mapping for map markers
const CATEGORY_ICON_MAP: { [key: string]: string } = {
  Restaurant: '🍽️',
  Café: '☕',
  Bäckerei: '🥖',
  Bar: '🍸',
  Blumenladen: '🌸',
  Geschenkeladen: '🎁',
  Parfümerie: '💐',
  Kosmetik: '💄',
  Weinhandlung: '🍷',
  Boutique: '👗',
  Konditorei: '🍰',
  Feinkost: '🧀',
  'Wellness & Spa': '💆',
  Sonstiges: '🏪',
};

@Component({
  selector: 'app-shop-map',
  standalone: true,
  imports: [CommonModule, FormsModule, NgClass, HttpClientModule],
  templateUrl: './shop-map.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./shop-map.component.css'],
})
export class ShopMapComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef;

  map: any;
  markers: any[] = [];
  searchCity: string = 'Köln';
  displayCity: string = 'Köln';
  selectedShop: Shop | null = null;
  hoveredShop: Shop | null = null;
  selectedCategory: string = 'all';
  mobileView: 'map' | 'list' = 'map'; // Mobile view toggle

  // Reactive state using BehaviorSubjects
  private shopsSubject = new BehaviorSubject<Shop[]>([]);
  shops$ = this.shopsSubject.asObservable();

  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  isLoading$ = this.isLoadingSubject.asObservable();

  private loadErrorSubject = new BehaviorSubject<string | null>(null);
  loadError$ = this.loadErrorSubject.asObservable();

  categories: string[] = ['all']; // Will be populated from API data

  // For JSON-LD structured data
  private jsonLdScript: HTMLScriptElement | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private meta: Meta,
    private title: Title,
  ) {}

  ngOnInit(): void {
    // Get city from URL parameter
    this.route.params.subscribe((params) => {
      const cityParam = params['city'];
      if (cityParam) {
        // Convert URL-friendly format to display format
        this.searchCity = this.urlToCity(cityParam);
        this.displayCity = this.searchCity;
      }

      // Set SEO meta tags
      this.updateSeoTags();

      // Load shops for the city
      this.loadShops();
    });

    this.loadGoogleMapsScript();
  }

  ngOnDestroy(): void {
    // Clean up JSON-LD script when component is destroyed
    if (this.jsonLdScript && this.jsonLdScript.parentNode) {
      this.jsonLdScript.parentNode.removeChild(this.jsonLdScript);
    }
  }

  private updateSeoTags(): void {
    const city = this.displayCity;
    const pageTitle = `Geschenkgutscheine in ${city} – Lokale Gutscheine kaufen | Schenkwerk`;
    const pageDescription = `Entdecke ${this.filteredShops.length || 'viele'} lokale Geschäfte in ${city}, die Geschenkgutscheine anbieten. Restaurants, Cafés, Wellness & mehr. Jetzt digitale Gutscheine kaufen und lokale Lieblingsgeschäfte unterstützen!`;
    const pageUrl = `https://gutscheine.schenkwerk.com/in/${this.cityToUrl(city)}`;

    // Set page title
    this.title.setTitle(pageTitle);

    // Update meta tags
    this.meta.updateTag({ name: 'description', content: pageDescription });
    this.meta.updateTag({
      name: 'keywords',
      content: `Geschenkgutscheine ${city}, Gutscheine ${city}, lokale Gutscheine, digitale Geschenkgutscheine, Restaurant Gutschein ${city}, Café Gutschein ${city}, Wellness Gutschein ${city}, Schenkwerk`,
    });
    this.meta.updateTag({ name: 'robots', content: 'index, follow' });
    this.meta.updateTag({ name: 'author', content: 'Schenkwerk' });

    // Open Graph tags for social sharing
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:description', content: pageDescription });
    this.meta.updateTag({ property: 'og:url', content: pageUrl });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: 'Schenkwerk' });
    this.meta.updateTag({ property: 'og:locale', content: 'de_DE' });
    this.meta.updateTag({
      property: 'og:image',
      content: 'https://schenkwerk.com/assets/og-image.png',
    });

    // Twitter Card tags
    this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
    this.meta.updateTag({ name: 'twitter:description', content: pageDescription });
    this.meta.updateTag({
      name: 'twitter:image',
      content: 'https://schenkwerk.com/assets/og-image.png',
    });

    // Canonical URL
    this.updateCanonicalUrl(pageUrl);
  }

  private updateCanonicalUrl(url: string): void {
    // Find or create canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', url);
  }

  private updateStructuredData(shops: Shop[]): void {
    // Remove existing JSON-LD script if present
    if (this.jsonLdScript && this.jsonLdScript.parentNode) {
      this.jsonLdScript.parentNode.removeChild(this.jsonLdScript);
    }

    const city = this.displayCity;
    const pageUrl = `https://gutscheine.schenkwerk.com/in/${this.cityToUrl(city)}`;

    // Create ItemList structured data for the shops
    const itemListData = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: `Geschenkgutscheine in ${city}`,
      description: `Lokale Geschäfte in ${city}, die Geschenkgutscheine anbieten`,
      url: pageUrl,
      numberOfItems: shops.length,
      itemListElement: shops.slice(0, 20).map((shop, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'LocalBusiness',
          name: shop.name,
          address: {
            '@type': 'PostalAddress',
            streetAddress: shop.address,
            addressLocality: city,
            addressCountry: 'DE',
          },
          ...(shop.url && { url: shop.url }),
          ...(shop.category && { category: this.getCategoryDisplay(shop.category) }),
          geo: {
            '@type': 'GeoCoordinates',
            latitude: shop.lat,
            longitude: shop.lng,
          },
        },
      })),
    };

    // Create WebPage structured data
    const webPageData = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: `Geschenkgutscheine in ${city}`,
      description: `Entdecke lokale Geschäfte in ${city}, die Geschenkgutscheine anbieten. Restaurants, Cafés, Wellness & mehr.`,
      url: pageUrl,
      inLanguage: 'de-DE',
      isPartOf: {
        '@type': 'WebSite',
        name: 'Schenkwerk',
        url: 'https://schenkwerk.com',
      },
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Schenkwerk',
            item: 'https://schenkwerk.com',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: `Gutscheine in ${city}`,
            item: pageUrl,
          },
        ],
      },
    };

    // Combine both structured data objects
    const structuredData = [webPageData, itemListData];

    // Create and append script element
    this.jsonLdScript = document.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(structuredData);
    document.head.appendChild(this.jsonLdScript);
  }

  // Convert URL format to display format - preserve umlaute
  private urlToCity(url: string): string {
    // Decode URI component to handle URL-encoded characters
    const decoded = decodeURIComponent(url);
    // Capitalize first letter
    return this.capitalize(decoded);
  }

  // Convert city name to URL format - preserve umlaute
  private cityToUrl(city: string): string {
    // Just lowercase and replace spaces with dashes
    // Umlaute (ä, ö, ü) will be preserved and URL-encoded by the browser
    return city.toLowerCase().replace(/\s+/g, '-');
  }

  private capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1);
  }

  ngAfterViewInit(): void {
    // Map will be initialized after Google Maps script loads
  }

  loadShops(): void {
    this.isLoadingSubject.next(true);
    this.loadErrorSubject.next(null);

    const apiUrl = `${environment.apiUrl}/public/shops?city=${encodeURIComponent(this.searchCity)}`;

    this.http
      .get<Shop[]>(apiUrl)
      .pipe(
        tap((shops) => {
          // Extract unique German category display names from shops
          const categorySet = new Set<string>(['all']);
          shops.forEach((shop) => {
            if (shop.category) {
              const displayCategory = this.getCategoryDisplay(shop.category);
              if (displayCategory) {
                categorySet.add(displayCategory);
              }
            }
          });
          this.categories = Array.from(categorySet).sort((a, b) => {
            if (a === 'all') return -1;
            if (b === 'all') return 1;
            if (a === 'Sonstiges') return 1;
            if (b === 'Sonstiges') return -1;
            return a.localeCompare(b, 'de');
          });

          // Update shops
          this.shopsSubject.next(shops);

          // Update SEO meta tags with shop count and structured data
          this.updateSeoTags();
          this.updateStructuredData(shops);

          // Update markers if map is already initialized
          if (this.map) {
            this.addMarkers(shops);

            // Center map on shops if available
            if (shops.length > 0) {
              this.centerMapOnShops(shops);
            }
          }
        }),
        catchError((error) => {
          console.error('Error loading shops:', error);
          this.loadErrorSubject.next(
            'Fehler beim Laden der Geschäfte. Bitte versuchen Sie es später erneut.',
          );
          this.shopsSubject.next([]);
          return of([]);
        }),
        finalize(() => {
          this.isLoadingSubject.next(false);
        }),
      )
      .subscribe();
  }

  centerMapOnShops(shops: Shop[]): void {
    if (!shops || shops.length === 0) return;

    const bounds = new (window as any).google.maps.LatLngBounds();
    shops.forEach((shop) => {
      bounds.extend(new (window as any).google.maps.LatLng(shop.lat, shop.lng));
    });

    this.map.fitBounds(bounds);

    // Ensure minimum zoom level
    const listener = (window as any).google.maps.event.addListener(this.map, 'idle', () => {
      if (this.map.getZoom() > 15) {
        this.map.setZoom(15);
      }
      (window as any).google.maps.event.removeListener(listener);
    });
  }

  loadGoogleMapsScript(): void {
    // Check if Google Maps is already loaded
    if (
      (window as any).google &&
      (window as any).google.maps &&
      (window as any).google.maps.marker
    ) {
      this.initMap();
      return;
    }

    // Check if script is already loading
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => {
        // Wait a bit for libraries to fully initialize
        setTimeout(() => this.initMap(), 100);
      });
      return;
    }

    // Create global callback for async loading
    (window as any).initMapCallback = () => {
      // Ensure marker library is loaded
      if ((window as any).google?.maps?.marker) {
        this.initMap();
      } else {
        console.error('Marker library not loaded');
        // Fallback: use setTimeout to wait for library
        setTimeout(() => {
          if ((window as any).google?.maps?.marker) {
            this.initMap();
          } else {
            alert('Failed to load Google Maps Marker library');
          }
        }, 500);
      }
    };

    // Load the script with proper async pattern and marker library
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDLpuuBXnz-1h6NvC6RC6PVzdYxFnzBYOs&libraries=marker&callback=initMapCallback`;
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      console.error('Failed to load Google Maps script');
      alert('Failed to load Google Maps. Please check your internet connection and API key.');
      delete (window as any).initMapCallback;
    };
    document.head.appendChild(script);
  }

  initMap(): void {
    // Default center (Cologne) - will be overridden if shops are loaded
    const defaultCenter = { lat: 50.9375, lng: 6.9603 };

    this.map = new (window as any).google.maps.Map(this.mapContainer.nativeElement, {
      zoom: 14,
      center: defaultCenter,
      mapTypeControl: true,
      streetViewControl: true,
      fullscreenControl: true,
      zoomControl: true,
      gestureHandling: 'greedy', // Allows panning without holding Ctrl/Cmd key
      mapId: 'schenkwerk_shop_map', // Required for AdvancedMarkerElement - styles must be set in Cloud Console
    });

    // Add markers for all shops
    const shops = this.shopsSubject.value;
    this.addMarkers(shops);

    // Center map on loaded shops (fixes race condition on mobile where shops load before map)
    if (shops.length > 0) {
      this.centerMapOnShops(shops);
    }
  }

  addMarkers(shops?: Shop[]): void {
    // Clear existing markers (for AdvancedMarkerElement)
    this.clearMarkers();

    // Use provided shops or get from filtered shops
    const shopsToRender = shops ? this.filterShops(shops) : this.filteredShops;

    // Add new markers using AdvancedMarkerElement (new API)
    shopsToRender.forEach((shop) => {
      const position = { lat: shop.lat, lng: shop.lng };

      // Create custom pin element for better styling
      const pinElement = document.createElement('div');
      pinElement.className = 'custom-marker';

      // Get category-specific icon
      const categoryIcon = this.getCategoryIcon(shop.category);

      if (shop.isSchenkwerkPartner) {
        // Schenkwerk partner marker (strong blue with category icon)
        pinElement.innerHTML = `
          <div style="
            width: 40px;
            height: 40px;
            background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
            border: 3px solid white;
            border-radius: 50% 50% 50% 0;
            transform: rotate(-45deg);
            box-shadow: 0 4px 8px rgba(0,0,0,0.3);
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            position: relative;
          ">
            <span style="transform: rotate(45deg); font-size: 18px;">${categoryIcon}</span>
            <span style="
              position: absolute;
              top: -6px;
              right: -6px;
              background: #facc15;
              border: 2px solid white;
              border-radius: 50%;
              width: 16px;
              height: 16px;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 10px;
              transform: rotate(45deg);
            ">⭐</span>
          </div>
        `;
      } else {
        // External shop marker (light blue with category icon)
        pinElement.innerHTML = `
          <div style="
            width: 34px;
            height: 34px;
            background: linear-gradient(135deg, #93c5fd 0%, #60a5fa 100%);
            border: 3px solid white;
            border-radius: 50% 50% 50% 0;
            transform: rotate(-45deg);
            box-shadow: 0 3px 6px rgba(0,0,0,0.2);
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
          ">
            <span style="transform: rotate(45deg); font-size: 16px;">${categoryIcon}</span>
          </div>
        `;
      }

      // Use AdvancedMarkerElement (new recommended API)
      const marker = new (window as any).google.maps.marker.AdvancedMarkerElement({
        map: this.map,
        position: position,
        content: pinElement,
        title: shop.name,
        zIndex: shop.isSchenkwerkPartner ? 1000 : 100,
      });

      // Create info window with enhanced styling
      const partnerBadge = shop.isSchenkwerkPartner
        ? `<span style="display: inline-block; background: #4F46E5; color: white; padding: 3px 6px; border-radius: 4px; font-size: 10px; font-weight: 600; margin-bottom: 6px;">
             ⭐ SCHENKWERK PARTNER
           </span>`
        : '';

      const logoUrl = this.getLogoUrl(shop);
      const logoHtml = logoUrl
        ? `<img src="${logoUrl}" alt="${shop.name}" style="width: 40px; height: 40px; border-radius: 50%; object-fit: cover; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.15); margin-right: 10px; flex-shrink: 0;" onerror="this.style.display='none'" />`
        : '';

      const websiteDomain = shop.url ? this.extractDomain(shop.url) : '';
      const websiteUrlWithUtm = shop.url ? this.addUtmSource(shop.url) : '';
      const websiteLink = shop.url
        ? `<p style="margin: 4px 0 0 0; font-size: 11px;">
             <a href="${websiteUrlWithUtm}" target="_blank" rel="noopener" style="color: #4F46E5; text-decoration: none;">
               ${websiteDomain}
             </a>
           </p>`
        : '';

      const voucherUrlWithUtm = shop.voucherUrl ? this.addUtmSource(shop.voucherUrl) : '';
      const voucherButton = shop.voucherUrl
        ? `<a href="${voucherUrlWithUtm}" target="_blank" rel="noopener" style="display: block; margin-top: 8px; padding: 6px 12px; background: #4F46E5; color: white; text-align: center; text-decoration: none; font-size: 11px; font-weight: 600; border-radius: 6px;">
             🎁 Gutschein kaufen
           </a>`
        : '';

      const categoryDisplay = this.getCategoryDisplay(shop.category);

      const infoWindow = new (window as any).google.maps.InfoWindow({
        headerDisabled: true,
        content: `
          <div style="max-width: 280px; line-height: 1.3;">
            <!-- Custom Header with Logo and Name -->
            <div style="display: flex; align-items: center; gap: 10px; padding-bottom: 8px; border-bottom: 1px solid #e5e7eb;">
              ${logoHtml}
              <div style="flex: 1; min-width: 0;">
                <h3 style="margin: 0; font-size: 14px; font-weight: 600; color: #1f2937; line-height: 1.2;">
                  ${shop.name}
                </h3>
                <p style="margin: 2px 0 0 0; font-size: 11px; color: #6b7280; font-weight: 500;">
                  ${categoryDisplay}
                </p>
              </div>
            </div>
            <!-- Content -->
            <div style="margin-top: 8px;">
              <p style="margin: 0; font-size: 11px; color: #6b7280; line-height: 1.3;">
                ${shop.address}
              </p>
            </div>
            ${shop.description ? `<p style="margin: 6px 0 0 0; font-size: 11px; color: #4b5563; line-height: 1.3;">${shop.description}</p>` : ''}
            ${websiteLink}
            ${voucherButton}
            ${partnerBadge ? `<div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb;">${partnerBadge}</div>` : ''}
          </div>
        `,
        maxWidth: 300,
      });

      // Click event - select shop
      marker.addListener('click', () => {
        // Close all other info windows
        this.markers.forEach((m) => {
          if (m.infoWindow) {
            m.infoWindow.close();
          }
        });

        // For AdvancedMarkerElement, we need to use the marker element directly
        infoWindow.open({
          map: this.map,
          anchor: marker,
        });
        this.selectedShop = shop;
        this.cdr.detectChanges(); // Force UI update
      });

      // Hover events - show info window temporarily
      pinElement.addEventListener('mouseenter', () => {
        infoWindow.open({
          map: this.map,
          anchor: marker,
        });
        this.hoveredShop = shop;
      });

      pinElement.addEventListener('mouseleave', () => {
        // Only close if this shop is not selected
        if (this.selectedShop?.id !== shop.id) {
          infoWindow.close();
        }
        this.hoveredShop = null;
      });

      marker.infoWindow = infoWindow;
      marker.shopId = shop.id; // Store shop ID for lookup
      this.markers.push(marker);
    });
  }

  searchLocation(): void {
    if (!this.searchCity.trim()) {
      return;
    }

    const geocoder = new (window as any).google.maps.Geocoder();
    geocoder.geocode({ address: this.searchCity }, (results: any[], status: string) => {
      if (status === 'OK' && results[0]) {
        this.map.setCenter(results[0].geometry.location);
        this.map.setZoom(14);

        // Update display city name
        const addressComponents = results[0].address_components;
        for (let component of addressComponents) {
          if (component.types.includes('locality')) {
            this.displayCity = component.long_name;

            // Update URL with new city
            const cityUrl = this.cityToUrl(this.displayCity);
            this.router.navigate(['/in', cityUrl], { replaceUrl: true });

            // Load shops for the new city
            this.loadShops();
            break;
          }
        }
      } else {
        alert('Standort nicht gefunden. Bitte versuchen Sie einen anderen Städtenamen.');
      }
    });
  }

  centerOnShop(shop: Shop): void {
    // Close all info windows first
    this.markers.forEach((m) => {
      if (m.infoWindow) {
        m.infoWindow.close();
      }
    });

    // Toggle selection: if already selected, deselect
    if (this.selectedShop?.id === shop.id) {
      this.selectedShop = null;
      this.cdr.detectChanges(); // Force UI update

      // Zoom out and re-center on all filtered shops with smooth animation
      const filtered = this.filteredShops;
      if (filtered.length > 0) {
        this.centerMapOnShops(filtered);
      }
      return;
    }

    // Deselect any previously selected shop and select the new one
    this.selectedShop = shop;
    this.cdr.detectChanges(); // Force UI update

    // Pan to the selected shop with smooth animation
    this.map.panTo({ lat: shop.lat, lng: shop.lng });

    // Smooth zoom animation
    const targetZoom = 15; // Less extreme zoom (was 17)
    const currentZoom = this.map.getZoom();

    // Gradually zoom in if needed
    if (currentZoom < targetZoom) {
      const zoomInterval = setInterval(() => {
        const zoom = this.map.getZoom();
        if (zoom < targetZoom) {
          this.map.setZoom(zoom + 1);
        } else {
          clearInterval(zoomInterval);
        }
      }, 100);
    } else if (currentZoom > targetZoom) {
      this.map.setZoom(targetZoom);
    }

    // Find and open the info window for the selected marker
    const marker = this.markers.find((m) => m.shopId === shop.id);
    if (marker && marker.infoWindow) {
      marker.infoWindow.open({
        map: this.map,
        anchor: marker,
      });
    }
  }

  getDirections(shop: Shop): void {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${shop.lat},${shop.lng}`;
    window.open(url, '_blank');
  }

  visitWebsite(shop: Shop, event?: Event, url?: string): void {
    if (event) {
      event.stopPropagation();
    }
    const targetUrl = url || shop.voucherUrl || shop.url;
    if (targetUrl) {
      window.open(targetUrl, '_blank');
    }
  }

  get filteredShops(): Shop[] {
    const shops = this.shopsSubject.value;
    return this.filterShops(shops);
  }

  private filterShops(shops: Shop[]): Shop[] {
    if (this.selectedCategory === 'all') {
      return shops;
    }
    return shops.filter((shop) => this.getCategoryDisplay(shop.category) === this.selectedCategory);
  }

  filterByCategory(category: string): void {
    this.selectedCategory = category;
    this.selectedShop = null;

    // Re-render markers with filtered shops
    if (this.map) {
      this.addMarkers();

      // Re-center map on filtered shops
      const filtered = this.filteredShops;
      if (filtered.length > 0) {
        this.centerMapOnShops(filtered);
      }
    }
  }

  get schenkwerkPartnerCount(): number {
    return this.shopsSubject.value.filter((shop) => shop.isSchenkwerkPartner).length;
  }

  getLogoUrl(shop: Shop): string | null {
    if (!shop.logoUrl) return null;
    return `${environment.apiUrl}/images/${shop.logoUrl}`;
  }

  getCategoryDisplay(category: string | null | undefined): string {
    if (!category) return '';
    const lowerCategory = category.toLowerCase();
    return CATEGORY_MAP[lowerCategory] || 'Sonstiges';
  }

  getCategoryIcon(category: string | null | undefined): string {
    if (!category) return CATEGORY_ICON_MAP['Sonstiges'];
    const displayCategory = this.getCategoryDisplay(category);
    return CATEGORY_ICON_MAP[displayCategory] || CATEGORY_ICON_MAP['Sonstiges'];
  }

  extractDomain(url: string | undefined): string {
    if (!url) return '';

    // Ensure URL has a protocol
    let fullUrl = url;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      fullUrl = environment.protocol + url;
    }

    try {
      const urlObj = new URL(fullUrl);
      return urlObj.hostname.replace('www.', '');
    } catch {
      return url;
    }
  }

  addUtmSource(url: string | undefined): string {
    if (!url) return '';

    // Ensure URL has a protocol
    let fullUrl = url;
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      fullUrl = environment.protocol + url;
    }

    try {
      const urlObj = new URL(fullUrl);
      urlObj.searchParams.set('utm_source', 'schenkwerk');
      return urlObj.toString();
    } catch {
      return fullUrl;
    }
  }

  getCategoryCount(category: string): number {
    if (category === 'all') {
      return this.shopsSubject.value.length;
    }
    return this.shopsSubject.value.filter(
      (shop) => this.getCategoryDisplay(shop.category) === category,
    ).length;
  }

  // Hover methods for shop list
  onShopHover(shop: Shop): void {
    this.hoveredShop = shop;

    // Find and open the corresponding marker's info window
    const marker = this.markers.find((m) => m.shopId === shop.id);
    if (marker && marker.infoWindow) {
      marker.infoWindow.open({
        map: this.map,
        anchor: marker,
      });

      // Optionally pan to marker (subtle animation)
      this.map.panTo({ lat: shop.lat, lng: shop.lng });
    }
  }

  onShopHoverEnd(shop: Shop): void {
    this.hoveredShop = null;

    // Only close info window if this shop is not selected
    if (this.selectedShop?.id !== shop.id) {
      const marker = this.markers.find((m) => m.shopId === shop.id);
      if (marker && marker.infoWindow) {
        marker.infoWindow.close();
      }
    }
  }

  // Helper to remove all markers (for AdvancedMarkerElement)
  private clearMarkers(): void {
    this.markers.forEach((marker) => {
      if (marker.map) {
        marker.map = null; // Remove from map
      }
    });
    this.markers = [];
  }

  // Mobile view switching
  switchMobileView(view: 'map' | 'list'): void {
    this.mobileView = view;
  }
}
